/**
 * sanitize-preview-html — the one gate between third-party document
 * converters and `dangerouslySetInnerHTML`.
 *
 * The in-app preview turns a docx into HTML with mammoth and a sheet into a
 * table with SheetJS. Both carry the source document's content faithfully,
 * and the source is user-supplied bytes: a `javascript:` hyperlink, an image
 * with an `onerror` handler, a pasted `<script>`. None of that may reach the
 * app's DOM, so every converter output goes through DOMPurify here with an
 * allowlist of exactly what a document needs — structure, text emphasis,
 * tables, lists, links, images — and nothing that runs, styles the page, or
 * submits.
 *
 * Fails closed: without a DOM (server render) or on a non-string, the
 * result is '' — never the input.
 */
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'ul', 'ol', 'li',
  'strong', 'em', 'b', 'i', 'u',
  'a', 'img', 'span', 'div', 'br', 'sup', 'sub', 'code', 'pre', 'blockquote',
];

const ALLOWED_ATTR = ['href', 'src', 'alt', 'title', 'colspan', 'rowspan', 'target'];

/** DOMPurify tests ALLOWED_URI_REGEXP against every allowed attribute that is
 *  not on its URI-safe list, so with a strict regexp `colspan="2"` and
 *  `target="_blank"` would be dropped as bad URLs. These carry no URL. */
const URI_SAFE_ATTR = ['colspan', 'rowspan', 'target'];

/** Links: https?/mailto. Images: https? or an inline data:image (mammoth
 *  embeds docx pictures that way). No relative or scheme-less URLs: a preview
 *  has no base to resolve them against. */
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|data:image\/)/i;

/** DOMPurify's own `data:` rule is per TAG (any data: URI is fine on <img>),
 *  which lets `data:text/html` through the regexp; the hook below narrows it
 *  to images. Whitespace and control characters are stripped the way
 *  DOMPurify does before it tests a URL. */
const ATTR_WHITESPACE = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;
const DATA_URI = /^data:/i;
const DATA_IMAGE_URI = /^data:image\//i;

let configured = false;

function purifier(): typeof DOMPurify | null {
  if (!DOMPurify.isSupported) return null;
  if (!configured) {
    DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
      if (data.attrName !== 'src' && data.attrName !== 'href') return;
      const value = data.attrValue.replace(ATTR_WHITESPACE, '');
      if (DATA_URI.test(value) && !DATA_IMAGE_URI.test(value)) data.keepAttr = false;
    });
    // Every anchor gets rel="noopener noreferrer": a target="_blank" link in a
    // preview must never hand the opener window to the destination.
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.nodeName === 'A') node.setAttribute('rel', 'noopener noreferrer');
    });
    configured = true;
  }
  return DOMPurify;
}

export function sanitizePreviewHtml(html: string): string {
  if (typeof html !== 'string' || html === '') return '';
  const purify = purifier();
  if (!purify) return '';
  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    ADD_URI_SAFE_ATTR: URI_SAFE_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    KEEP_CONTENT: true,
  });
}
